import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, CreditCard, LockKeyhole, MapPin, Route, ShieldCheck, Sparkles, Users } from "lucide-react";
import { Reveal } from "@/components/Primitives";
import { InnerHero, CTABand } from "@/components/Sections";
import PaymentLogos from "@/components/PaymentLogos";
import { HERO_SLIDES, ITINERARIES, NORTH_INDIA_DEMO } from "@/data/site";
import { CURRENCIES, useCurrency } from "@/hooks/useCurrency";

const CATEGORIES = ["Holidays", "Long Haul Tours", "Wildlife Holidays"];
export default function Itineraries() {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const { currency, setCurrency, ratesReady, format } = useCurrency();

  const deals = useMemo(() => ITINERARIES.filter((item) => item.category === category), [category]);
  return (
    <>
      <InnerHero
        overline="Six Private Tour Deals"
        title="India journeys, ready to tailor"
        subtitle="Holiday escapes, long-haul journeys and wildlife routes, all designed as 15-day private itineraries."
        image={HERO_SLIDES[2].img}
      />

      <section className="bg-coconut py-20 md:py-28" data-testid="north-india-demo">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="overline text-gold">Demo North India Itinerary</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-forest">{NORTH_INDIA_DEMO.title}</h2>
            <p className="mt-5 text-navy/65 leading-relaxed max-w-xl">{NORTH_INDIA_DEMO.subtitle}</p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm text-navy/70">
              <span className="inline-flex items-center gap-2"><CalendarDays size={17} className="text-teal" />{NORTH_INDIA_DEMO.days}</span>
              <span className="inline-flex items-center gap-2"><Route size={17} className="text-teal" />Private, customisable route</span>
            </div>
            <div className="mt-9 space-y-5">
              {NORTH_INDIA_DEMO.stages.map((stage, index) => (
                <div key={stage.days} className="grid grid-cols-[2.5rem_1fr] gap-3 sm:gap-4 items-start">
                  <div className="relative">
                    <span className="w-8 h-8 rounded-full bg-forest text-ivory text-xs font-bold grid place-items-center">{index + 1}</span>
                    {index < NORTH_INDIA_DEMO.stages.length - 1 && <span className="absolute left-[0.95rem] top-9 w-px h-9 bg-sand" />}
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gold">{stage.days}</p>
                    <h3 className="font-serif text-xl text-forest">{stage.place}</h3>
                    <p className="mt-1 text-sm text-navy/60 leading-relaxed">{stage.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-forest text-ivory px-6 py-3 text-sm font-semibold hover:bg-teal transition-colors">Request this itinerary <ArrowRight size={15} /></Link>
              <span className="text-sm font-semibold text-navy/55">Price on request</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 grid-rows-2 gap-3 h-[400px] sm:h-[520px] md:h-[620px]">
              {NORTH_INDIA_DEMO.images.map((image, index) => (
                <figure key={image.label} className={`relative overflow-hidden rounded-lg ${index === 0 ? "row-span-2" : ""}`}>
                  <img src={image.src} alt={image.alt} className="w-full h-full object-cover" />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/80 to-transparent px-5 pb-4 pt-12 text-ivory font-semibold text-sm">{image.label}</figcaption>
                </figure>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28" data-testid="itineraries-grid">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <p className="overline text-gold">Choose your travel style</p>
              <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Tour categories">
                {CATEGORIES.map((item) => (
                  <button key={item} type="button" role="tab" aria-selected={category === item} onClick={() => setCategory(item)} className={`px-5 py-2.5 rounded-full border text-sm font-semibold transition-colors ${category === item ? "bg-forest border-forest text-ivory" : "border-sand bg-coconut text-navy/65 hover:border-gold"}`}>
                    {item}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 text-sm text-navy/65">
              <span>Prices in your currency</span>
              <select value={currency} onChange={(event) => setCurrency(event.target.value)} className="min-h-11 bg-coconut border border-sand rounded-lg px-4 py-2.5 font-semibold text-forest outline-none focus:border-gold">
                {CURRENCIES.map((item) => <option key={item} disabled={item !== "USD" && !ratesReady}>{item}</option>)}
              </select>
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {deals.map((it, i) => (
              <Reveal key={it.title} delay={i * 0.08}>
                <article className="group h-full rounded-lg border border-sand bg-coconut overflow-hidden flex flex-col" data-testid={`itinerary-card-${i}`}>
                  <div className="relative h-64 overflow-hidden">
                    <img src={it.img} alt={it.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/65 to-transparent" />
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      {it.badges.map((badge) => <span key={badge} className="rounded-full bg-ivory/95 text-forest text-[0.62rem] font-semibold tracking-widest uppercase px-3 py-1">{badge}</span>)}
                    </div>
                  </div>
                  <div className="p-5 sm:p-7 flex flex-col flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <h2 className="font-serif text-2xl md:text-3xl text-forest leading-tight max-w-sm">{it.title}</h2>
                      <div className="text-left sm:text-right">
                        <p className="text-forest font-bold">From {format(it.priceUsd)}</p>
                        <label className="mt-1 inline-flex items-center gap-1 text-[0.68rem] text-navy/45">
                          <span>Currency</span>
                          <select aria-label={`Currency for ${it.title}`} value={currency} onChange={(event) => setCurrency(event.target.value)} className="min-h-10 bg-transparent font-semibold text-forest outline-none">
                            {CURRENCIES.map((item) => <option key={item} disabled={item !== "USD" && !ratesReady}>{item}</option>)}
                          </select>
                        </label>
                      </div>
                    </div>
                    {currency !== "USD" && <p className="mt-2 text-xs text-navy/45">USD reference: ${it.priceUsd.toLocaleString("en-US")}</p>}
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <Meta icon={Clock} label="Duration" value={it.days} />
                      <Meta icon={Sparkles} label="Travel style" value={it.level} />
                      <Meta icon={MapPin} label="Destinations" value={it.places} />
                      <Meta icon={Users} label="Best for" value={it.bestFor} />
                    </div>
                    <Link to="/contact" className="mt-7 inline-flex items-center gap-2 self-start rounded-full bg-forest text-ivory px-6 py-3 text-sm font-semibold tracking-wide transition-transform hover:-translate-y-0.5 hover:bg-teal">
                      Request Full Itinerary <ArrowRight size={15} />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-xs text-navy/45">Prices are starting references from the original packages. Your final 15-day quotation will reflect season, occupancy, room choice and itinerary customisation. Currency conversions use daily reference rates from Frankfurter.</p>
        </div>
      </section>

      <section className="bg-sand/35 py-16">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-3 gap-8">
          <Trust icon={ShieldCheck} title="Secure payment process" text="Itemised invoices and verified payment instructions for every confirmed booking." />
          <Trust icon={CreditCard} title="Flexible payment options" text="Card and bank payment options are confirmed clearly in your personal proposal." />
          <Trust icon={LockKeyhole} title="Clear booking terms" text="Deposit, balance, cancellation and refund terms are shared before you pay." />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-12">
          <p className="overline text-gold mb-5">Secure Online Payments</p>
          <div className="max-w-2xl">
            <PaymentLogos />
          </div>
        </div>
      </section>

      <CTABand overline="Custom itineraries" title="Need something bespoke?" subtitle="Share your brief and we will design a private India journey around your dates, pace and interests." primary={{ label: "Request Custom Itinerary", to: "/contact" }} secondary={{ label: "Visa & Payment Info", to: "/travel-info" }} />
    </>
  );
}

const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2"><Icon size={15} className="text-teal mt-0.5 shrink-0" /><div><p className="text-navy/45 text-xs">{label}</p><p className="text-navy/80 font-medium">{value}</p></div></div>
);

const Trust = ({ icon: Icon, title, text }) => (
  <div className="flex gap-4"><div className="w-11 h-11 shrink-0 rounded-lg bg-forest text-ivory grid place-items-center"><Icon size={20} /></div><div><h3 className="font-serif text-xl text-forest">{title}</h3><p className="mt-1 text-sm text-navy/60 leading-relaxed">{text}</p></div></div>
);
